import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ISector, ISectorSaveRequest } from '../interfaces/sector.interface';
import { SectoresService } from '../services/sectores.service';
import { SessionService } from 'src/app/shared/helpers/sesion.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-sector-form',
  templateUrl: './sector-form.component.html',
  providers: [MessageService]
})
export class SectorFormComponent implements OnInit, OnChanges {
  @Input() model: ISector | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;
  displayProFormaDialog: boolean = false;
  displayComentariosDialog: boolean = false;
  displayFinanzasDialog: boolean = false;
  displayAnalisisDialog: boolean = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private sectoresService: SectoresService,
    private sessionService: SessionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    // Si no hay modelo, cargar valores por defecto
    if (!this.model) {
      this.loadDefaultValues();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      this.initForm();
      if (changes['model'].currentValue) {
        this.loadSector();
      } else {
        // Si es modo creación, cargar valores por defecto
        this.loadDefaultValues();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      sector: [{ value: '', disabled: !!this.model }, [Validators.required]],
      descripcion: ['', [Validators.required]],
      responsable: [{ value: '', disabled: true }],
      fechaAlta: [{ value: '', disabled: true }]
    });
  }

  loadSector(): void {
    if (this.model) {
      this.form.patchValue({
        sector: this.model.sector,
        descripcion: this.model.descripcion,
        responsable: this.model.responsable,
        fechaAlta: this.model.fechaAlta
      });
    }
  }

  loadDefaultValues(): void {
    // Cargar valores por defecto para modo creación
    this.form.patchValue({
      responsable: this.sessionService.getUserSession(),
      fechaAlta: this.formatDate(new Date())
    });
  }

  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'Sector',
        sClave: `${this.form.get('sector')?.value}`,
        sNombre: this.form.get('descripcion')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  openProForma(): void {
    this.displayProFormaDialog = true;
  }

  onProFormaDialogClosed(): void {
    this.displayProFormaDialog = false;
  }

  openComentarios(): void {
    this.displayComentariosDialog = true;
  }

  onComentariosDialogClosed(): void {
    this.displayComentariosDialog = false;
  }

  openFinanzas(): void {
    this.displayFinanzasDialog = true;
  }

  onFinanzasDialogClosed(): void {
    this.displayFinanzasDialog = false;
  }

  openAnalisis(): void {
    this.displayAnalisisDialog = true;
  }

  onAnalisisDialogClosed(): void {
    this.displayAnalisisDialog = false;
  }

  create(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      
      const request: ISectorSaveRequest = {
        que: 1, // Crear
        tope: "",
        sector: formValue.sector,
        descripcion: formValue.descripcion,
        fechaAlta: this.formatDate(new Date()), // Fecha actual en formato dd/mm/yyyy
        responsable: this.sessionService.getUserSession() // Usuario actual sin comillas
      };

      this.sectoresService.createSector(request).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.onSave.emit({
              success: true,
              message: 'Sector creado correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al crear el sector'
            });
            this.onSave.emit({
              success: false,
              message: 'Error al crear el sector'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear el sector'
          });
          this.onSave.emit({
            success: false,
            message: 'Error al crear el sector'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  update(): void {
    if (this.form.valid && this.model) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      
      const request: ISectorSaveRequest = {
        que: 2, // Actualizar
        tope: "",
        sector: this.model.sector, // Usar el valor original para la búsqueda
        descripcion: formValue.descripcion,
        fechaAlta: this.model.fechaAlta, // Mantener la fecha original
        responsable: this.model.responsable // Mantener el responsable original
      };

      this.sectoresService.updateSector(request).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.onSave.emit({
              success: true,
              message: 'Sector actualizado correctamente'
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Error al actualizar el sector'
            });
            this.onSave.emit({
              success: false,
              message: 'Error al actualizar el sector'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el sector'
          });
          this.onSave.emit({
            success: false,
            message: 'Error al actualizar el sector'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }
}
